import {useEffect, useMemo, useState} from 'react'
import {createPortal} from 'react-dom'

import {useNavigate} from 'react-router-dom'
import {useTranslation} from 'react-i18next'

import {Modal} from '../../../../../_metronic/elements/Modal'
import {openAlert} from '../../../../../_metronic/elements/NotificationAlert'
import {useErrorHandler} from '../../../../../_metronic/helpers/ErrorHandler'
import {useDrawV2} from '../../Map/Draw/DrawV2Context'
import {DrawTools} from '../../Map/Draw/DrawState'
import {useMapLayersV2} from '../../Map/MapLayersV2Context'
import {mapState} from '../../Map/MapState'
import {useSearchContext} from '../../Toolbar/SearchContext'
import {useIsMobile} from '../../../../shared/responsive'
import {LandDraftBuilder} from '../LandDraftBuilder'
import * as FieldService from '../services/FieldService'
import * as LandService from '../services/LandService'
import '../CreateFlow.style.scss'

const FLOW_STEP = {
  NAME: 'name',
  METHOD: 'method',
  MANUAL: 'manual',
  AUTOMATIC: 'automatic',
}

const CREATION_METHOD = {
  MANUAL: 'manual',
  AUTOMATIC: 'automatic',
}

const validateFieldName = (name, t) => {
  const value = (name || '').trim()

  if (value.length < 1) {
    return t('fields:create.input.name.errors.short')
  }

  if (value.length > 32) {
    return t('fields:create.input.name.errors.too_long')
  }

  return null
}

const MethodOption = ({title, description, selected, onClick}) => {
  return (
    <button
      type='button'
      className={`gis-create-flow-option ${
        selected ? 'gis-create-flow-option--selected' : ''
      }`.trim()}
      onClick={onClick}
    >
      <span className='gis-create-flow-option__title'>{title}</span>
      {description ? <span className='gis-create-flow-option__copy'>{description}</span> : null}
    </button>
  )
}

const DesktopFieldFlowShell = ({children}) => {
  return (
    <div className='right-gis-container pb-5'>
      <div className='d-flex flex-column flex-row-fluid w-400px gis-create-flow-desktop-shell'>
        <div className='d-flex flex-column-auto displayable mb-4 gis-create-flow-desktop-shell__content'>
          {children}
        </div>
      </div>
    </div>
  )
}

const ManualFieldCard = ({loading, hasPolygon, onCancel, onSubmit}) => {
  const {t} = useTranslation()

  return (
    <div className='card shadow-sm field-view-container right-card-data gis-create-flow-card'>
      <div className='card-header'>
        <h4 className='gis-create-flow-title'>{t('fields:create.manual.title')}</h4>
      </div>

      <div className='card-footer'>
        <button
          type='button'
          className='btn btn-light gis-create-flow-footer-button'
          onClick={onCancel}
          disabled={loading}
        >
          {t('common:actions.cancel')}
        </button>
        <button
          type='button'
          className='btn btn-primary gis-create-flow-footer-button'
          onClick={onSubmit}
          disabled={!hasPolygon || loading}
        >
          {!loading && t('fields:create.buttons.create')}
          {loading && (
            <span className='indicator-progress' style={{display: 'block'}}>
              {t('fields:create.messages.creating')}
              <span className='spinner-border spinner-border-sm align-middle ms-2'></span>
            </span>
          )}
        </button>
      </div>
    </div>
  )
}

const ManualDrawingGuide = ({visible, pointCount}) => {
  const {t} = useTranslation()
  const message = useMemo(() => {
    if (pointCount < 1) {
      return t('fields:create.manual.guide_first')
    }

    if (pointCount < 3) {
      return t('fields:create.manual.guide_continue')
    }

    return t('fields:create.manual.guide_finish')
  }, [pointCount, t])

  if (!visible) {
    return null
  }

  if (typeof document === 'undefined') {
    return null
  }

  return createPortal(
    <div className='gis-map-guidance'>
      <div className='gis-map-guidance__card'>{message}</div>
    </div>,
    document.body
  )
}

const CreateField = () => {
  const {t} = useTranslation()
  const [step, setStep] = useState(FLOW_STEP.NAME)
  const [method, setMethod] = useState(null)
  const [name, setName] = useState('')
  const [nameError, setNameError] = useState(null)
  const [loading, setLoading] = useState(false)
  const [polygonDraw, setPolygonDraw] = useState(null)
  const [manualPointCount, setManualPointCount] = useState(0)
  const {errorHandler} = useErrorHandler()
  const mapLayers = useMapLayersV2()
  const navigate = useNavigate()
  const draw = useDrawV2()
  const {startDrawing, endDrawing, mode: setDrawMode} = draw
  const search = useSearchContext()
  const isMobile = useIsMobile()
  const hasPolygon = Boolean(polygonDraw?.geometry)
  const showSetupModal = step === FLOW_STEP.NAME || step === FLOW_STEP.METHOD

  const cancelFlow = () => {
    draw.endDrawing()
    setPolygonDraw(null)
    setManualPointCount(0)
    navigate('/gis')
  }

  const continueFromName = () => {
    const nextError = validateFieldName(name, t)
    setNameError(nextError)

    if (nextError != null) {
      return
    }

    setStep(FLOW_STEP.METHOD)
  }

  const continueFromMethod = () => {
    if (method == null) {
      return
    }

    setStep(method === CREATION_METHOD.MANUAL ? FLOW_STEP.MANUAL : FLOW_STEP.AUTOMATIC)
  }

  useEffect(() => {
    if (step !== FLOW_STEP.MANUAL) {
      return
    }

    setPolygonDraw(null)
    setManualPointCount(0)

    startDrawing({
      tools: [DrawTools.POLYGON],
      onCreate: (event) => {
        const feature = event.features[0]
        setPolygonDraw(feature)
        setManualPointCount(0)
      },
      onUpdate: (event) => {
        setPolygonDraw(event.features[0])
      },
      onDelete: () => {
        setPolygonDraw(null)
        setManualPointCount(0)
      },
    })
    setDrawMode(DrawTools.POLYGON)

    let disposed = false
    let timerId = null
    let container = null

    const onCanvasClick = () => {
      setManualPointCount((previousCount) => previousCount + 1)
    }

    const bindCanvasClicks = () => {
      if (disposed) {
        return
      }

      const nextContainer = mapState?.map?.getCanvasContainer?.()

      if (!(nextContainer instanceof HTMLElement)) {
        timerId = window.setTimeout(bindCanvasClicks, 120)
        return
      }

      container = nextContainer
      container.addEventListener('click', onCanvasClick)
    }

    bindCanvasClicks()

    return () => {
      disposed = true

      if (timerId != null) {
        window.clearTimeout(timerId)
      }

      if (container instanceof HTMLElement) {
        container.removeEventListener('click', onCanvasClick)
      }

      endDrawing()
    }
  }, [endDrawing, setDrawMode, startDrawing, step])

  useEffect(() => {
    if (step === FLOW_STEP.MANUAL || step === FLOW_STEP.AUTOMATIC) {
      document.getElementById('kt_content_container')?.scroll({top: 0, behavior: 'smooth'})
    }
  }, [step])

  const createFieldManually = async () => {
    if (!polygonDraw?.geometry) {
      return
    }

    setLoading(true)
    const res = await errorHandler(FieldService.create(name.trim(), polygonDraw.geometry))

    if (res?.ok) {
      search.load()
      mapLayers.reloadSource()
      openAlert('success', 'Campo creado', `El campo ${res.data.data.name} se creó correctamente.`)
      navigate(`/gis/field/view/${res.data.data.field_id}`)
    }

    setLoading(false)
  }

  const createFieldAutomatically = async ({pendingLands, unionGeometry}) => {
    if (!unionGeometry) {
      openAlert('danger', 'Error', 'No pudimos construir el perímetro del campo con esos lotes.')
      return
    }

    setLoading(true)

    const fieldRes = await errorHandler(FieldService.create(name.trim(), unionGeometry))

    if (!fieldRes?.ok) {
      setLoading(false)
      return
    }

    const createdField = fieldRes.data.data
    const landsRes = await errorHandler(
      LandService.create(createdField.field_id, {
        lands: pendingLands,
      })
    )

    if (!landsRes?.ok) {
      const rollbackRes = await errorHandler(FieldService.deleteField(createdField.field_id))

      setLoading(false)

      if (rollbackRes?.ok) {
        mapLayers.reloadSource()
        search.load()
        openAlert(
          'danger',
          'No pudimos crear el campo',
          'Fallo la carga automatica de los lotes y revertimos el campo para evitar datos incompletos.'
        )
        navigate('/gis')
        return
      }

      mapLayers.reloadSource()
      search.load()
      openAlert(
        'danger',
        'Campo creado con lotes pendientes',
        'El campo se creó, pero no pudimos cargar los lotes automáticamente ni revertirlo.'
      )
      navigate(`/gis/field/view/${createdField.field_id}`)
      return
    }

    setLoading(false)
    mapLayers.reloadSource()
    search.load()
    openAlert('success', 'Campo creado', `El campo ${createdField.name} se creó con sus lotes.`)
    navigate(`/gis/field/view/${createdField.field_id}`)
  }

  const stageContent = (() => {
    if (step === FLOW_STEP.MANUAL) {
      return (
        <>
          <ManualDrawingGuide visible={!hasPolygon} pointCount={manualPointCount} />
          <ManualFieldCard
            loading={loading}
            hasPolygon={hasPolygon}
            onCancel={cancelFlow}
            onSubmit={createFieldManually}
          />
        </>
      )
    }

    if (step === FLOW_STEP.AUTOMATIC) {
      return (
        <LandDraftBuilder
          title={t('fields:buttons.add_lands')}
          baseName={name.trim()}
          startIndex={0}
          submitLabel={t('fields:create.buttons.create')}
          loading={loading}
          onCancel={cancelFlow}
          onSubmit={createFieldAutomatically}
        />
      )
    }

    return null
  })()

  return (
    <>
      <Modal open={showSetupModal}>
        <div className='modal-dialog modal-dialog-centered'>
          <div className='modal-content'>
            <div className='modal-header p-5'>
              <h5 className='modal-title'>{t('fields:buttons.create_field')}</h5>
            </div>

            <div className='modal-body p-5'>
              {step === FLOW_STEP.NAME && (
                <div className='gis-create-flow-modal-step'>
                  <div className='gis-create-flow-input'>
                    <input
                      autoFocus
                      type='text'
                      className={`form-control ${nameError ? 'is-invalid' : ''}`.trim()}
                      placeholder={t('fields:create.input.name.placeholder')}
                      value={name}
                      onChange={(event) => {
                        setName(event.target.value)
                        if (nameError != null) {
                          setNameError(null)
                        }
                      }}
                      onKeyDown={(event) => {
                        if (event.key === 'Enter') {
                          event.preventDefault()
                          continueFromName()
                        }
                      }}
                    />
                    {nameError ? <div className='invalid-feedback d-block'>{nameError}</div> : null}
                  </div>
                </div>
              )}

              {step === FLOW_STEP.METHOD && (
                <div className='gis-create-flow-modal-step'>
                  <div className='gis-create-flow-options'>
                    <MethodOption
                      title={t('fields:create.method.manual_title')}
                      description={t('fields:create.method.manual_description')}
                      selected={method === CREATION_METHOD.MANUAL}
                      onClick={() => setMethod(CREATION_METHOD.MANUAL)}
                    />
                    <MethodOption
                      title={t('fields:create.method.automatic_title')}
                      description={t('fields:create.method.automatic_description')}
                      selected={method === CREATION_METHOD.AUTOMATIC}
                      onClick={() => setMethod(CREATION_METHOD.AUTOMATIC)}
                    />
                  </div>
                </div>
              )}
            </div>

            <div className='modal-footer'>
              {step === FLOW_STEP.METHOD && (
                <button
                  type='button'
                  className='btn btn-light'
                  onClick={() => setStep(FLOW_STEP.NAME)}
                >
                  {t('common:actions.back')}
                </button>
              )}
              <button type='button' className='btn btn-light' onClick={cancelFlow}>
                {t('common:actions.cancel')}
              </button>
              {step === FLOW_STEP.NAME && (
                <button type='button' className='btn btn-primary' onClick={continueFromName}>
                  {t('common:actions.continue')}
                </button>
              )}
              {step === FLOW_STEP.METHOD && (
                <button
                  type='button'
                  className='btn btn-primary'
                  onClick={continueFromMethod}
                  disabled={method == null}
                >
                  {t('common:actions.continue')}
                </button>
              )}
            </div>
          </div>
        </div>
      </Modal>

      {showSetupModal || stageContent == null ? null : isMobile ? (
        stageContent
      ) : (
        <DesktopFieldFlowShell>{stageContent}</DesktopFieldFlowShell>
      )}
    </>
  )
}

export default CreateField
